import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, PlugZap, Save, ShieldCheck, ShieldAlert, Database, FlaskConical } from "lucide-react";

export default function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState({
    host_url: "", sap_client: "", system_id: "",
    username: "", password: "", sync_frequency: "manual",
    msme_field: "", msme_values: "", billing_types: "", credit_billing_types: "", insecure_tls: false, data_start: "2026-07-31",
    gst_gl_revenue: "", gst_gl_igst: "", gst_gl_cgst: "", gst_gl_sgst: "",
    gst_gl_itc_igst: "", gst_gl_itc_cgst: "", gst_gl_itc_sgst: "",
  });
  const [services, setServices] = useState([]);
  const [mvtMap, setMvtMap] = useState({});
  const [mvtCats, setMvtCats] = useState([]);
  const [mvtDefaults, setMvtDefaults] = useState([]);
  const [newMvt, setNewMvt] = useState("");
  const [newCat, setNewCat] = useState("purchases");
  const [probe, setProbe] = useState(null);
  const [probing, setProbing] = useState(false);
  const [syncLogs, setSyncLogs] = useState([]);
  const [openLog, setOpenLog] = useState(0);
  const loadSyncLogs = () => api.get("/system/sync/logs").then(({ data }) => setSyncLogs(data.logs || [])).catch(() => {});
  const runProbe = async () => {
    setProbing(true); setProbe(null);
    try {
      await api.post("/settings/sap/stock-probe");
      const t0 = Date.now();
      while (Date.now() - t0 < 10 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 4000));
        const { data: job } = await api.get("/settings/sap/stock-probe/result");
        if (job.status === "done") { setProbe(job.result); return; }
        if (job.status === "failed") { setProbe({ detail: job.error || "Probe failed" }); return; }
      }
      setProbe({ detail: "Probe is taking unusually long — try again later" });
    } catch (e) {
      setProbe({ detail: e.response?.data?.detail || e.message });
    } finally { setProbing(false); }
  };
  const [meta, setMeta] = useState({ has_password: false, verified: false, last_test: null });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingSvc, setTestingSvc] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [msmeTesting, setMsmeTesting] = useState(false);
  const [msmeResult, setMsmeResult] = useState(null);
  const [glDetecting, setGlDetecting] = useState(false);
  const [glDetect, setGlDetect] = useState(null);

  const detectGls = async () => {
    setGlDetecting(true);
    setGlDetect(null);
    const finish = (res) => {
      setGlDetect(res);
      if (res?.saved && Object.keys(res.saved).length) setForm((f) => ({ ...f, ...res.saved }));
      if (res?.message) toast[res.ok ? "success" : "warning"](res.message);
      setGlDetecting(false);
    };
    const poll = async () => {
      try {
        const { data: j } = await api.get("/settings/sap/detect-gst-gls/status");
        if (j.status === "done" && j.result) finish(j.result);
        else if (j.status === "failed") finish({ ok: false, message: j.error || "Trial-balance scan failed" });
        else setTimeout(poll, 5000);
      } catch (e) {
        finish({ ok: false, message: e.message });
      }
    };
    try {
      const { data } = await api.post("/settings/sap/detect-gst-gls", {});
      if (data.building) setTimeout(poll, 4000);
      else finish(data);
    } catch (e) {
      finish({ ok: false, message: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    }
  };

  const testMsme = async () => {
    setMsmeTesting(true);
    setMsmeResult(null);
    try {
      const { data } = await api.post("/settings/sap/test-msme", {}, { timeout: 120000 });
      setMsmeResult(data);
    } catch (e) {
      setMsmeResult({ ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setMsmeTesting(false);
    }
  };
  const [liveStatus, setLiveStatus] = useState([]);

  const loadLiveStatus = () =>
    api.get("/settings/sap/live-status").then(({ data }) => setLiveStatus(data.items)).catch(() => {});

  useEffect(() => {
    api.get("/settings/sap").then(({ data }) => {
      const { services: svcs, movement_map, movement_categories, movement_defaults, ...rest } = data;
      setForm((f) => ({ ...f, ...rest, password: "" }));
      setServices(svcs || []);
      setMvtMap(movement_map || {});
      setMvtCats(movement_categories || []);
      setMvtDefaults(movement_defaults || []);
      setMeta({ has_password: data.has_password, verified: data.verified, last_test: data.last_test });
    });
    loadLiveStatus();
    loadSyncLogs();
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/sap", { ...form, movement_map: mvtMap });
      toast.success(data.message || "SAP connection settings saved");
      if (data.host_url !== undefined) setForm((f) => ({ ...f, host_url: data.host_url, sap_client: data.sap_client || f.sap_client }));
      api.get("/settings/sap").then(({ data: d }) => setServices(d.services || []));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const testServices = async () => {
    setTestingSvc(true);
    try {
      const { data } = await api.post("/settings/sap/test-services");
      setServices((svcs) => svcs.map((s) => {
        const r = data.results.find((x) => x.key === s.key);
        return r ? { ...s, status: r.status, detail: r.detail } : s;
      }));
      toast[data.mapped > 0 ? "success" : "warning"](`${data.mapped} of ${data.total} APIs mapped & reachable`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setTestingSvc(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/settings/sap/test");
      setTestResult(data);
    } catch (e) {
      setTestResult({ status: "failed", message: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setTesting(false);
    }
  };

  const field = "mt-1.5 w-full bg-white border border-slate-300 rounded-lg px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:border-[#B01E24]/60 disabled:opacity-50";
  const label = "text-xs font-semibold uppercase tracking-wider text-slate-500";

  const canView = user?.role === "admin" || (user?.permissions || []).includes("settings");
  if (!canView)
    return (
      <div className="text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded-md p-4 max-w-lg" data-testid="settings-no-access">
        You don't have access to SAP Connection settings. Ask an admin to grant it.
      </div>
    );

  return (
    <div className="max-w-4xl space-y-6" data-testid="sap-settings-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">SAP Cloud Connection</h1>
        <p className="text-sm text-slate-500 mt-1">
          S/4HANA Private Cloud · OData connection for Dee Piping. Until connected & verified, the app serves realistic mock data.
        </p>
      </div>

      <div className={`flex items-center gap-3 rounded-lg border p-4 text-sm ${meta.verified ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`} data-testid="connection-status-banner">
        {meta.verified ? <ShieldCheck size={18} /> : <Database size={18} />}
        {meta.verified
          ? "SAP connection verified. Live data mapping can now be enabled."
          : "Running on MOCK data — configure and test your SAP host to switch to live data."}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-6 space-y-4">
        {!isAdmin && <div className="text-xs text-amber-600">Read-only: only admins can modify SAP connection settings.</div>}
        <div>
          <label className={label}>SAP Host URL</label>
          <input disabled={!isAdmin} value={form.host_url} onChange={set("host_url")} data-testid="sap-host-input" className={field} placeholder="https://my-s4hana.deepiping.com:44300" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={label}>Client</label>
            <input disabled={!isAdmin} value={form.sap_client} onChange={set("sap_client")} data-testid="sap-client-input" className={field} placeholder="100" />
          </div>
          <div>
            <label className={label}>System ID</label>
            <input disabled={!isAdmin} value={form.system_id} onChange={set("system_id")} data-testid="sap-sid-input" className={field} placeholder="S4P" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={label}>Username (Communication User)</label>
            <input disabled={!isAdmin} value={form.username} onChange={set("username")} data-testid="sap-username-input" className={field} placeholder="COMM_USER" />
          </div>
          <div>
            <label className={label}>Password {meta.has_password && <span className="text-slate-400 normal-case">(saved — leave blank to keep)</span>}</label>
            <input disabled={!isAdmin} type="password" value={form.password} onChange={set("password")} data-testid="sap-password-input" className={field} placeholder="••••••••" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={label}>Sync Frequency</label>
            <select disabled={!isAdmin} value={form.sync_frequency} onChange={set("sync_frequency")} data-testid="sap-sync-select" className={field}>
              <option value="manual">Manual</option>
              <option value="hourly">Hourly</option>
              <option value="daily">Daily</option>
            </select>
          </div>
          <div>
            <label className={label}>Go-live / Data Start (dashboards default from this date)</label>
            <input disabled={!isAdmin} type="date" value={form.data_start || ""} onChange={set("data_start")}
              data-testid="sap-data-start-input" className={field} />
          </div>
        </div>
        <label className="flex items-start gap-2.5 pt-1 cursor-pointer">
          <input type="checkbox" disabled={!isAdmin} checked={!!form.insecure_tls}
            onChange={(e) => setForm({ ...form, insecure_tls: e.target.checked })}
            data-testid="sap-insecure-tls-toggle" className="mt-0.5 accent-[#B01E24]" />
          <span className="text-xs text-slate-600">
            <span className="font-medium text-slate-700">Skip TLS certificate verification</span> — leave OFF unless the
            connection test fails with a certificate error (turning this on exposes SAP credentials to interception)
          </span>
        </label>
        {isAdmin && (
          <div className="flex gap-3 pt-2">
            <button onClick={save} disabled={saving} data-testid="sap-save-button"
              className="flex items-center gap-2 bg-[#B01E24] hover:bg-[#9A171D] disabled:opacity-60 text-white text-sm font-medium rounded-lg px-4 py-2 transition-colors">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Settings
            </button>
            <button onClick={test} disabled={testing} data-testid="sap-test-button"
              className="flex items-center gap-2 bg-white hover:bg-slate-50 disabled:opacity-60 text-slate-700 border border-slate-300 text-sm rounded-lg px-4 py-2 transition-colors">
              {testing ? <Loader2 size={14} className="animate-spin" /> : <PlugZap size={14} />} Test Connection
            </button>
          </div>
        )}
        {testResult && (
          <div
            data-testid="sap-test-result"
            className={`flex items-start gap-2.5 rounded-lg border p-3.5 text-sm ${
              testResult.status === "connected"
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : testResult.status === "mock"
                ? "border-cyan-300 bg-cyan-50 text-cyan-800"
                : "border-red-200 bg-red-50 text-red-800"
            }`}
          >
            {testResult.status === "connected" ? <ShieldCheck size={16} className="mt-0.5 flex-shrink-0" /> : <ShieldAlert size={16} className="mt-0.5 flex-shrink-0" />}
            {testResult.message}
          </div>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-6 space-y-4" data-testid="msme-settings-card">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">MSME Identification (Business Partner master)</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Name the Business Partner field that marks a vendor as MSME. SE11 table-field names work — e.g. <span className="font-mono">MINDK</span> (table LFB1) is auto-matched to the API name <span className="font-mono">MinorityGroup</span> on A_SupplierCompany / A_Supplier.
            Suppliers whose value matches the list (e.g. <span className="font-mono">S3</span>) are treated as MSME. The AP Ageing page shows exactly how the field resolved and how many suppliers matched.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={label}>MSME field name (SE11 or OData name)</label>
            <input disabled={!isAdmin} value={form.msme_field || ""} onChange={set("msme_field")} data-testid="msme-field-input" className={field} placeholder="e.g. MINDK (LFB1) or YY1_MSMEUdyamNo_bus" />
          </div>
          <div>
            <label className={label}>Values that count as MSME <span className="text-slate-400 normal-case">(optional, comma separated)</span></label>
            <input disabled={!isAdmin} value={form.msme_values || ""} onChange={set("msme_values")} data-testid="msme-values-input" className={field} placeholder="leave blank = any non-empty value" />
          </div>
          <div>
            <label className={label}>Billing types for Sales / Margin <span className="text-slate-400 normal-case">(F2 + returns, credit &amp; debit notes)</span></label>
            <input disabled={!isAdmin} value={form.billing_types || ""} onChange={set("billing_types")} data-testid="billing-types-input" className={field} placeholder="F2,F1,RE,G2,L2" />
          </div>
          <div>
            <label className={label}>Credit-note billing types <span className="text-slate-400 normal-case">(these SUBTRACT from sales — your tenant bills sales as G2, so keep G2 out)</span></label>
            <input disabled={!isAdmin} value={form.credit_billing_types || ""} onChange={set("credit_billing_types")} data-testid="credit-billing-types-input" className={field} placeholder="RE,CBRE,S1" />
          </div>
        </div>
        {isAdmin && (
          <div className="mt-4">
            <button onClick={testMsme} disabled={msmeTesting} data-testid="test-msme-button"
              className="flex items-center gap-1.5 text-xs bg-[#B01E24] hover:bg-[#8e181d] disabled:opacity-60 text-white rounded-md px-3 py-1.5 transition-colors">
              {msmeTesting ? <Loader2 size={12} className="animate-spin" /> : <FlaskConical size={12} />} Test MSME lookup
            </button>
            {msmeResult && (
              msmeResult.ok ? (
                <div className="mt-3 text-[12px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2" data-testid="msme-test-ok">
                  <span className="font-semibold">Working:</span> {msmeResult.status.configured_field} → <span className="font-mono">{msmeResult.status.entity}.{msmeResult.status.resolved_field}</span> · {msmeResult.status.suppliers_marked} supplier(s) marked MSME
                  {msmeResult.sample?.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {msmeResult.sample.map((x) => (
                        <span key={x.supplier} className="font-mono text-[10.5px] bg-white border border-emerald-200 rounded px-1.5 py-0.5">{x.supplier}: {x.value}</span>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-3 text-[12px] text-amber-900 bg-amber-50 border border-amber-300 rounded-md px-3 py-2" data-testid="msme-test-error">
                  {msmeResult.error || msmeResult.status?.error || "MSME lookup failed"}
                </div>
              )
            )}
          </div>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-6 space-y-4" data-testid="gst-gl-settings-card">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">GSTR ↔ GL Reconciliation Mapping</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            GL accounts matched against GSTR-1 (output) and GSTR-2 ITC (input) on the GL Reconciliation tabs. Comma-separated codes and ranges work — e.g. <span className="font-mono">400000-409999, 310500</span>.
            Leave blank to auto-detect by GL account name (IGST / CGST / SGST / Sales); the tab shows exactly which accounts were counted. After changing, hit Save Settings above.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={label}>Revenue GL accounts</label>
            <input disabled={!isAdmin} value={form.gst_gl_revenue || ""} onChange={set("gst_gl_revenue")} data-testid="gst-gl-revenue-input" className={field} placeholder="e.g. 400000-409999" />
          </div>
          <div>
            <label className={label}>Output IGST GL accounts</label>
            <input disabled={!isAdmin} value={form.gst_gl_igst || ""} onChange={set("gst_gl_igst")} data-testid="gst-gl-igst-input" className={field} placeholder="e.g. 231001" />
          </div>
          <div>
            <label className={label}>Output CGST GL accounts</label>
            <input disabled={!isAdmin} value={form.gst_gl_cgst || ""} onChange={set("gst_gl_cgst")} data-testid="gst-gl-cgst-input" className={field} placeholder="e.g. 231002" />
          </div>
          <div>
            <label className={label}>Output SGST GL accounts</label>
            <input disabled={!isAdmin} value={form.gst_gl_sgst || ""} onChange={set("gst_gl_sgst")} data-testid="gst-gl-sgst-input" className={field} placeholder="e.g. 231003" />
          </div>
          <div>
            <label className={label}>Input / ITC IGST GL accounts</label>
            <input disabled={!isAdmin} value={form.gst_gl_itc_igst || ""} onChange={set("gst_gl_itc_igst")} data-testid="gst-gl-itc-igst-input" className={field} placeholder="e.g. 153001" />
          </div>
          <div>
            <label className={label}>Input / ITC CGST GL accounts</label>
            <input disabled={!isAdmin} value={form.gst_gl_itc_cgst || ""} onChange={set("gst_gl_itc_cgst")} data-testid="gst-gl-itc-cgst-input" className={field} placeholder="e.g. 153002" />
          </div>
          <div>
            <label className={label}>Input / ITC SGST GL accounts</label>
            <input disabled={!isAdmin} value={form.gst_gl_itc_sgst || ""} onChange={set("gst_gl_itc_sgst")} data-testid="gst-gl-itc-sgst-input" className={field} placeholder="e.g. 153003" />
          </div>
        </div>
        {isAdmin && (
          <div>
            <button onClick={detectGls} disabled={glDetecting} data-testid="detect-gst-gls-button"
              className="flex items-center gap-1.5 text-xs bg-[#B01E24] hover:bg-[#8e181d] disabled:opacity-60 text-white rounded-md px-3 py-1.5 transition-colors">
              {glDetecting ? <Loader2 size={12} className="animate-spin" /> : <FlaskConical size={12} />} Detect from SAP trial balance
            </button>
            <p className="text-[11px] text-slate-400 mt-1">Scans the last two months of journal entries (read-only) and fills the fields above with the GL accounts it recognises.</p>
            {glDetect && (
              glDetect.ok ? (
                <div className="mt-3 text-[12px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 space-y-1" data-testid="gst-gl-detect-ok">
                  <div className="font-semibold">{glDetect.message} (scanned {glDetect.months_scanned?.join(", ")})</div>
                  {Object.entries(glDetect.detected || {}).map(([h, accts]) => (
                    <div key={h} className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 w-20">{h}</span>
                      {accts.map((a) => <span key={a.gl} className="font-mono text-[10.5px] bg-white border border-emerald-200 rounded px-1.5 py-0.5">{a.gl}{a.name ? ` · ${a.name}` : ""}</span>)}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-3 text-[12px] text-amber-900 bg-amber-50 border border-amber-300 rounded-md px-3 py-2" data-testid="gst-gl-detect-error">{glDetect.message}</div>
              )
            )}
          </div>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="movement-map-card">
        <div className="px-5 py-4 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-800">MB51 Movement Type → Category Mapping</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Drives the Stock Reconciliation columns (Opening → Purchases, Prod In/Cons, Sales, Direct, Transfers, Misc → Derived vs Actual Closing).
            Standard SAP defaults are pre-filled — adjust any movement type and hit Save Settings above.
          </p>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="movement-map-grid">
            {Object.keys(mvtMap).sort().map((mt) => (
              <div key={mt} className="flex items-center gap-2 border border-slate-200 rounded-md px-2.5 py-1.5">
                <span className="font-mono text-xs text-slate-700 w-8">{mt}</span>
                <select disabled={!isAdmin} value={mvtMap[mt]} data-testid={`mvt-map-${mt}`}
                  onChange={(e) => setMvtMap({ ...mvtMap, [mt]: e.target.value })}
                  className="flex-1 bg-white text-xs text-slate-600 focus:outline-none">
                  {mvtCats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
                {isAdmin && !mvtDefaults.includes(mt) && (
                  <button onClick={() => { const m = { ...mvtMap }; delete m[mt]; setMvtMap(m); }} data-testid={`mvt-remove-${mt}`}
                    title="Remove custom movement type" className="text-slate-300 hover:text-red-600 text-xs font-bold px-0.5">✕</button>
                )}
              </div>
            ))}
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2 mt-4">
              <input value={newMvt} onChange={(e) => setNewMvt(e.target.value.replace(/\D/g, "").slice(0, 3))} data-testid="mvt-new-input"
                placeholder="Mvt type e.g. 309" className="bg-white border border-slate-300 rounded-md px-3 py-1.5 text-xs w-32 focus:outline-none focus:border-[#B01E24]/50" />
              <select value={newCat} onChange={(e) => setNewCat(e.target.value)} data-testid="mvt-new-cat"
                className="bg-white border border-slate-300 rounded-md px-3 py-1.5 text-xs focus:outline-none">
                {mvtCats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
              <button onClick={() => { if (newMvt) { setMvtMap({ ...mvtMap, [newMvt]: newCat }); setNewMvt(""); } }} data-testid="mvt-add-button"
                className="text-xs font-medium bg-white hover:bg-slate-50 text-[#B01E24] border border-[#B01E24]/40 rounded-md px-3 py-1.5 transition-colors">
                + Add movement type
              </button>
              <span className="text-[11px] text-slate-400">then Save Settings</span>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="sap-service-mapping">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">API Service Mapping</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Standard S/4HANA APIs used by each dashboard — managed by the app, nothing to configure. Map each API once in your SAP communication arrangements. All access is <span className="font-semibold text-slate-700">strictly read-only (GET)</span> — the app never writes back to SAP.
            </p>
          </div>
          {isAdmin && (
            <button onClick={testServices} disabled={testingSvc} data-testid="sap-test-services-button"
              className="flex items-center gap-2 bg-[#B01E24] hover:bg-[#9A171D] disabled:opacity-60 text-white text-xs font-medium rounded-lg px-3.5 py-2 transition-colors whitespace-nowrap">
              {testingSvc ? <Loader2 size={13} className="animate-spin" /> : <PlugZap size={13} />} Test All Services
            </button>
          )}
        </div>
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              {["API / Used For", "OData Service Path", "Status"].map((h) => (
                <th key={h} className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {services.map((s) => (
              <tr key={s.key} className="border-b border-slate-100 align-top">
                <td className="px-4 py-2.5 w-64">
                  <div className="font-medium text-slate-800">{s.label}</div>
                  <div className="text-[11px] text-slate-400">{s.used_for}</div>
                </td>
                <td className="px-4 py-2.5">
                  <span data-testid={`svc-path-${s.key}`} className="font-mono text-[11px] text-slate-500">{s.path}</span>
                </td>
                <td className="px-4 py-2.5 w-56">
                  <span data-testid={`svc-status-${s.key}`} className={`inline-block text-[10.5px] font-medium px-2 py-0.5 rounded-full border ${
                    s.status === "mapped" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : s.status === "not_mapped" ? "bg-amber-50 text-amber-700 border-amber-300"
                    : s.status === "untested" ? "bg-slate-100 text-slate-500 border-slate-200"
                    : "bg-red-50 text-red-700 border-red-200"
                  }`}>
                    {s.status === "mapped" ? "Mapped ✓" : s.status === "not_mapped" ? "Not mapped" : s.status === "untested" ? "Untested" : s.status === "auth_failed" ? "Auth failed" : s.status === "forbidden" ? "No authorisation" : "Unreachable"}
                  </span>
                  {s.detail && s.status !== "mapped" && s.status !== "untested" && (
                    <div className="text-[10.5px] text-slate-400 mt-1">{s.detail}</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isAdmin && (
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="live-data-health">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Live Data Health</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Per-report result of the last data fetch. If a report fell back to mock, the exact SAP error is shown here — open the report first, then refresh this panel.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={runProbe} disabled={probing} data-testid="stock-probe-button"
                className="flex items-center gap-2 bg-[#B01E24] hover:bg-[#9A171D] disabled:opacity-60 text-white text-xs rounded-lg px-3.5 py-2 transition-colors">
                {probing ? "Probing SAP…" : "Stock Data Probe"}
              </button>
              <button onClick={loadLiveStatus} data-testid="live-status-refresh-button"
                className="flex items-center gap-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 text-xs rounded-lg px-3.5 py-2 transition-colors">
                Refresh
              </button>
            </div>
          </div>
          {probe && (
            <div className="px-5 py-4 border-b border-slate-200 bg-slate-50/60" data-testid="stock-probe-result">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-2">Stock pipeline probe (read-only) — window from {probe.window_from}</div>
              {probe.detail ? (
                <div className="text-xs text-red-700 font-mono">{probe.detail}</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1 text-xs text-slate-700">
                  <div>MB51 headers: <b>{probe.headers_fetched}</b></div>
                  <div>Items parsed: <b>{probe.items_total}</b></div>
                  <div>Items with qty: <b className={probe.items_with_qty === 0 ? "text-red-700" : ""}>{probe.items_with_qty}</b></div>
                  <div>Items with value: <b className={probe.items_with_value === 0 ? "text-red-700" : "text-emerald-700"}>{probe.items_with_value}</b></div>
                  <div className="col-span-2">Amount field detected: <b className="font-mono">{probe.amount_field_detected}</b></div>
                  <div className="col-span-2">Posting dates: <b>{probe.posting_date_min || "—"} → {probe.posting_date_max || "—"}</b></div>
                  <div>Stock rows (MB5B qty): <b>{probe.stock_rows ?? probe.stock_error ?? "—"}</b></div>
                  <div>Valuation prices (MBEW): <b className={probe.valuation_prices_found === 0 ? "text-red-700" : "text-emerald-700"}>{probe.valuation_prices_found ?? probe.valuation_rows ?? probe.valuation_error ?? "—"}</b></div>
                  <div>Billing items: <b>{probe.billing_items ?? probe.billing_error ?? "—"}</b></div>
                  {probe.valuation_error && probe.valuation_prices_found === 0 && (
                    <div className="col-span-2 sm:col-span-4 text-[11px] text-red-700 font-mono" data-testid="probe-valuation-error">Valuation: {probe.valuation_error}</div>
                  )}
                  {probe.truncated_months?.length > 0 && (
                    <div className="col-span-2 sm:col-span-4 text-[11px] text-amber-700" data-testid="probe-truncated-months">
                      ⚠ Monthly fetch cap reached for: {probe.truncated_months.join(", ")} — oldest documents of these months may be missing
                    </div>
                  )}
                  {probe.headers_per_month && (
                    <div className="col-span-2 sm:col-span-4 text-[11px] text-slate-500">
                      Docs per month: {Object.entries(probe.headers_per_month).map(([m, n]) => `${m}: ${n}`).join(" · ")}
                    </div>
                  )}
                  {probe.movement_types?.length > 0 && (
                    <div className="col-span-2 sm:col-span-4 text-[11px] text-slate-500 font-mono break-words">
                      Movement types → category: {probe.movement_types.map((t) => `${t.mtype}(${t.items})→${t.category}`).join(" · ")}
                    </div>
                  )}
                  {probe.amount_field_candidates && Object.keys(probe.amount_field_candidates).length > 0 && (
                    <div className="col-span-2 sm:col-span-4 text-[11px] text-slate-500 font-mono">
                      Amount candidates (non-zero counts): {Object.entries(probe.amount_field_candidates).map(([k, n]) => `${k}=${n}`).join(", ")}
                    </div>
                  )}
                  {probe.sample_item_fields?.length > 0 && (
                    <div className="col-span-2 sm:col-span-4 text-[10.5px] text-slate-400 font-mono break-words">Item fields: {probe.sample_item_fields.join(", ")}</div>
                  )}
                </div>
              )}
            </div>
          )}
          <table className="w-full text-[12.5px]">
            <tbody>
              {liveStatus.map((r) => (
                <tr key={r.report} className="border-b border-slate-100 align-top">
                  <td className="px-4 py-2.5 w-64 font-medium text-slate-800">{r.title}</td>
                  <td className="px-4 py-2.5 w-36 whitespace-nowrap">
                    <span data-testid={`live-status-${r.report}`} className={`inline-block text-[10.5px] font-medium px-2 py-0.5 rounded-full border ${
                      r.status === "SAP" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : r.status === "UPLOAD" ? "bg-sky-50 text-sky-700 border-sky-200"
                      : r.status === "MOCK" ? "bg-amber-50 text-amber-700 border-amber-300"
                      : "bg-slate-100 text-slate-500 border-slate-200"
                    }`}>
                      {r.status === "SAP" ? "SAP Live ✓" : r.status === "UPLOAD" ? "Uploaded files" : r.status === "MOCK" ? "Mock fallback" : "Not fetched yet"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-[11.5px] text-slate-500 font-mono break-words">{r.error || (r.status === "SAP" ? "OK" : "—")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="extraction-log-card">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Extraction Log</h3>
            <p className="text-xs text-slate-500 mt-0.5">Every "Sync SAP Data" run — duration, records fetched and per-report outcome</p>
          </div>
          <button onClick={loadSyncLogs} data-testid="sync-logs-refresh" className="text-xs bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 rounded-lg px-3.5 py-2 transition-colors">Refresh</button>
        </div>
        {syncLogs.length === 0 ? (
          <div className="px-5 py-6 text-sm text-slate-400" data-testid="sync-logs-empty">No extraction runs yet — press "Sync SAP Data" in the top bar to run one.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {syncLogs.map((lg, i) => {
              const sap = lg.results.filter((r) => r.status === "sap").length;
              const fails = lg.results.filter((r) => r.status === "failed").length;
              const rows = lg.results.reduce((s, r) => s + r.rows, 0);
              const runState = lg.status || "ok";
              const badge = runState === "interrupted" ? ["INTERRUPTED", "bg-red-50 text-red-700 border-red-200"]
                : runState === "failed" ? ["FAILED", "bg-red-50 text-red-700 border-red-200"]
                : runState === "running" ? ["RUNNING…", "bg-sky-50 text-sky-700 border-sky-200"]
                : fails ? [`${fails} failed`, "bg-red-50 text-red-700 border-red-200"]
                : sap ? [`${sap} live from SAP`, "bg-emerald-50 text-emerald-700 border-emerald-200"]
                : ["mock data", "bg-amber-50 text-amber-700 border-amber-300"];
              return (
                <div key={lg.ts}>
                  <button onClick={() => setOpenLog(openLog === i ? -1 : i)} data-testid={`sync-log-row-${i}`}
                    className="w-full flex flex-wrap items-center gap-3 px-5 py-3 text-left hover:bg-slate-50 transition-colors">
                    <span className="text-xs font-mono text-slate-600 w-44">{new Date(lg.ts).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
                    <span className="text-xs text-slate-500">{((lg.total_ms || 0) / 1000).toFixed(1)}s</span>
                    <span className="text-xs font-mono text-slate-700">{rows.toLocaleString("en-IN")} records</span>
                    <span className={`text-[10.5px] font-medium px-2 py-0.5 rounded-full border ${badge[1]}`} data-testid={`sync-log-status-${i}`}>
                      {badge[0]}
                    </span>
                    {lg.resumed && (
                      <span className="text-[10.5px] font-medium px-2 py-0.5 rounded-full border bg-violet-50 text-violet-700 border-violet-200" data-testid={`sync-log-resumed-${i}`}>
                        RESUMED AFTER RESTART
                      </span>
                    )}
                    <span className="text-[11px] text-slate-400 ml-auto">{lg.user} · {openLog === i ? "hide" : "details"}</span>
                  </button>
                  {openLog === i && (
                    <div className="px-5 pb-3">
                      {lg.error && (
                        <div className="mb-2 text-[11.5px] text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid={`sync-log-error-${i}`}>{lg.error}</div>
                      )}                      <table className="w-full text-[12px]">
                        <tbody>
                          {lg.results.map((r) => (
                            <tr key={r.report} className="border-t border-slate-100">
                              <td className="py-1.5 w-56 text-slate-700">{r.title}</td>
                              <td className="py-1.5 w-28">
                                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${r.status === "sap" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : r.status === "upload" ? "bg-sky-50 text-sky-700 border-sky-200" : r.status === "failed" ? "bg-red-50 text-red-700 border-red-200" : "bg-amber-50 text-amber-700 border-amber-300"}`}>{r.status.toUpperCase()}</span>
                              </td>
                              <td className="py-1.5 w-28 text-right font-mono text-slate-600">{r.rows.toLocaleString("en-IN")} rows</td>
                              <td className="py-1.5 w-20 text-right font-mono text-slate-400">{r.ms} ms</td>
                              <td className="py-1.5 pl-4 text-[11px] text-slate-500">{r.message}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
